import axios from '../axios'
import { checkResponse, getProjectId, checkResponseErr } from '../utils'

/**
 * @function getUcaseTabs 获取案件详情的 tab
 * @param {Number} id 案件 id
 * @returns {Array} tabList
 */
const getUcaseTabs = (id) => {
  return axios
    .get(
      `/api/v1/projects/${getProjectId()}/ucases/${id}/steps?datetimeFormat=datetime&view=web`
    )
    .then(checkResponse)
}

/**
 * @function getUcaseDetails 获取案件详情
 * @param {String} url tab中获取到的 url
 * @returns {Object}} detail {uiSpec, data}
 */
const getUcaseDetails = (url) => {
  return axios.get(`/api/v1${url}`).then(checkResponse)
}

/**
 * @function getUcaseReviews 获取审核详情
 * @param {Number} id 案件 id
 * @returns {Object}} res {actions: 操作,  title: 标题, redispatchReason: 退回原因, cancel: 是否可以撤销}
 */
const getUcaseReviews = (id) => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/ucases/${id}/reviews/current`)
    .then(checkResponse)
}

/**
 * @function postUcaseReviews 获取审核详情
 * @param {Number} id 案件 id
 * @param {Object} data 审核数据
 * @param {String} slug action 中的 slug
 */
const postUcaseReviews = (id, data, slug) => {
  return axios
    .post(
      `/api/v1/projects/${getProjectId()}/ucases/${id}/reviews?review_action=${slug}`,
      {
        data,
      }
    )
    .then(checkResponse)
}


/**
 * @function getUcaseList 获取案件列表
 * @param {String} points 点位
 * @param {String} search 格式化后的查询条件
 * @param {String} order 排序的字段和顺序
 * @param {Number} page 页码
 * @param {Number} per 每页显示条数
 * @returns {Object} ucaseList {columns, data, pagination}
 */
const getUcaseList = (points, search, page = 1, per = 10, order=null, projectId = getProjectId()) => {
  return axios
    .get(
      `/api/v1/projects/${projectId}/ucases?datetimeFormat=datetime&page=${page}&per=${per}${
        points ? `&${points}` : ''
      }${search ? `&${search}` : ''}${order ? `&order=${order}` : ''}`
    )
    .then(checkResponse)
}

const getQuickUcaseList = (query, projectId = getProjectId()) => {
  return axios
    .get(
      `/api/v1/projects/${projectId}/ucases?datetimeFormat=datetime&${query}`
    )
    .then(checkResponse)
}

/**
 * @function getUcaseColumns 获取案件列表表头
 */
const getUcaseColumns = () => {
  return axios
    .get(
      `/api/v1/projects/${getProjectId()}/config/ucaseColumns`
    )
    .then(checkResponse)
}

/**
 * @function getUcaseQuickList 通过 es 接口获取案件列表
 * @param {String} points 点位
 * @param {String} search 格式化后的查询条件
 * @param {String} order 排序的字段和顺序
 * @param {Number} page 页码
 * @param {Number} per 每页显示条数
 * @returns {Object} ucaseList {columns, data, pagination}
 */
const getUcaseQuickList = (points, search, page = 1, per = 10, order=null) => {
  return axios
    .get(
      `/api/v1/projects/${getProjectId()}/search?datetimeFormat=datetime&page=${page}&per=${per}${
        points ? `&${points}` : ''
      }${search ? `&${search}` : ''}${order ? `&order=${order}` : ''}`
    )
    .then(checkResponse)
}

/**
 * @function getUcaseStatusList 获取所有状态
 * @returns {Array} statusList
 */
const getUcaseStatusList = () => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/ucases/all_stages?as=selects`)
    .then(checkResponse)
}

/**
 * @function deleteUcase 删除案件
 * @param {Number} id 案件 id
 */
const deleteUcase = (id) => {
  return axios
    .delete(`/api/v1/projects/${getProjectId()}/ucases/${id}`)
    .then(checkResponse)
}

/**
 * @function createUcase 新建案件
 * @param {Object} data
 */
const createUcase = (data) => {
  return axios
    .post(`/api/v1/projects/${getProjectId()}/ucases`, { data })
    .then(checkResponse)
}

/**
 * @function updateUcase 编辑案件
 * @param {Number} id ucase id
 * @param {Object} data
 * @returns {Object} res {deatil}
 */
const updateUcase = (id, data) => {
  return axios
    .put(`/api/v1/projects/${getProjectId()}/ucases/${id}`, { data })
    .then(checkResponse)
}

/**
 * @function updateReview 编辑案件
 * @param {Number} id ucase id
 * @param {Object} data
 * @returns {Object} res {deatil}
 */
const updateReview = (uid,rid, data) => {
  return axios
    .put(`/api/v1/projects/${getProjectId()}/ucases/${uid}/reviews/${rid}?review_action=review_reform_reform3`, { data })
    .then(checkResponse)
}

/**
 * @function updateReview 编辑案件
 * @param {Number} id ucase id
 * @param {Object} data
 * @returns {Object} res {deatil}
 */
const updateCommonReview = (uid,rid,action, data) => {
  return axios
    .put(`/api/v1/projects/${getProjectId()}/ucases/${uid}/reviews/${rid}?review_action=${action}`, { data })
    .then(checkResponse)
}
/**
 * @function getUcaseMap 获取案件地图数据
 * @param {String} points 点位
 * @param {String} search 格式化后的查询条件
 * @returns {Object} data 地图数据
 */
const getUcaseMap = (points, search) => {
  return axios
    .get(
      `/api/v1/projects/${getProjectId()}/ucases/index_quick?datetimeFormat=datetime${
        points ? `&${points}` : ''
      }${search ? `&${search}` : ''}`
    )
    .then(checkResponse)
}

/**
 * @function getCountByStage 获取案件地图数据
 * @returns {Array} res.data {count: 状态中的案件数量, title: 状态名称}
 */
const getCountByStage = (query) => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/ucases/count_by_stage${query ? `?${query}`: ''}`)
    .then(checkResponse)
}


/**
 * @function getUcaseAreaCount 获取点位数量
 * @returns {Array} res.counts 点位数量
 */
const getUcaseAreaCount = (query) => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/ucases/group_by?fields=area_id${query ? `&${query}`:''}`)
    .then(checkResponse)
}
/**
 * @function getUcaseStandardCount 获取指标数量
 * @returns {Array} res.counts 指标数量
 */
 const getUcaseStandardCount = (query) => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/ucases/group_by?fields=standard_id${query ? `&${query}`:''}`)
    .then(checkResponse)
}

/**
 * @function postUcaseFile 上传案件模板
 * @param {String} file 上传的文件
 * @returns {String} msg 消息
 */
const postUcaseFile = (file) => {
  return axios({
    url: `/api/v1/projects/${getProjectId()}/connection/import/file`,
    method: 'post',
    data: file,
  }).then(checkResponse)
}

/**
 * @function postJHFile 上传案件模板
 * @param {String} file 上传的文件
 * @returns {String} msg 消息
 */
const postJHFile = (file, id) => {
  return axios({
    url: `/api/v1/projects/${getProjectId()}/connection/jianhang/file?taskId=${id}`,
    method: 'post',
    data: file,
  }).then(checkResponse)
}
/**
 * @function deleteUcaseAction 撤回操作
 * @param {String} uri
 */
const deleteUcaseAction = (uri) => {
  return axios.delete(`/api/v1${uri}`).then(checkResponse)
}

/**
 * @function caseDetails
 * @desc 每个案件的详细信息(id=ucase_id)方法
 * @param {Number} projectId 项目id
 * @param {Number} ucaseId 案例id
 * @returns {Object} data 返回数据
 * @returns {String} data.title 返回案例标题
 * @returns {String} data.description 返回案例描述
 * @returns {Object} data.images 返回案例图片
 * @returns {Number} data.ucase_id 返回案例id
 * @returns {Number} data.tnode_id 返回案例树形结构中末级ID
 */
function caseDetails (ucaseId) {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/ucases?id=${ucaseId}`)
    .then(checkResponse)
    .catch(checkResponseErr)
}
/**
 * @function checkIsExistUcase
 * @desc 是否存在案件
 * @returns {Object}
 */
function checkIsExistUcase() {
  return axios.get(`/api/v1/projects/${getProjectId()}/exist_ucase`).then(checkResponse);
}

/**
 * @function revokeUcaseAction
 * @desc 撤销操作
 * @returns {Object}
 */
function revokeUcaseAction(uri) {
  return axios.delete(`/api/v1${uri}`).then(checkResponse);
}

/**
 * @function getUcaseSearchOption
 * @desc 获取通用配置搜索项
 * @returns {Object}
 */
const getUcaseSearchOption = () => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/configs?slug=review_selects`)
    .then(checkResponse)
}

/**
 * @function getUcaseEarlyWarningConfig
 * @desc 根据搜索条件获取高发点位的配置内容
 * @returns {Object}
 */
const getUcaseEarlyWarningConfig = (query) => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/toparea/detail?${query}`)
    .then(checkResponse)
}

/**
 * @function getUcaseListByPost
 * @desc 适用于高发点位 - 使用 post 方式请求 ucase
 * @returns {Object}
 */
const getUcaseListByPost = (ids, page = 1, per = 10) => {
  return axios
    .post(`/api/v1/projects/${getProjectId()}/ucases/bulk_search`, {
      ids,
      page,
      per,
    })
    .then(checkResponse)
}

/**
 * @function getShunyiReport
 * @desc 获取顺义项目报告
 * @returns {Object}
 */
const getShunyiReport = () => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/download/shunyiReport`)
    .then(checkResponse)
}

/**
 * @function getUcaseAreaRoad
 * @desc 获取发生道路
 * @returns {Object}
 */
const getUcaseAreaRoad = () => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/area/roads?utype=tree`)
    .then(checkResponse)
}

/**
 * @function getDeleteUcaseList
 * @desc 获取删除后的案件列表
 * @returns {Object}
 */
const getDeleteUcaseList = (points, search, page = 1, per = 10, order=null) => {
  return axios
    .get(
      `/api/v1/projects/${getProjectId()}/ucases?query_deleted=true&page=${page}&per=${per}${
        points ? `&${points}` : ''
      }${search ? `&${search}` : ''}${order ? `&order=${order}` : ''}`
    )
    .then(checkResponse)
}

/**
 * @function getDeleteUcaseTabs
 * @desc 获取删除案件后的 tabs
 * @returns {Object}
 */
const getDeleteUcaseTabs = (id) => {
  return axios
    .get(
      `/api/v1/projects/${getProjectId()}/ucases/${id}/steps?query_deleted=true&datetimeFormat=datetime&view=web`
    )
    .then(checkResponse)
}

/**
 * @function recoverUcase
 * @desc 恢复案件
 * @returns {Object}
 */
const recoverUcase = (ids) => {
  return axios
    .put(`/api/v1/projects/${getProjectId()}/ucases/bulk_recover`, { ids })
    .then(checkResponse)
}

/**
 * @function getUcaseLogs
 * @desc 获取操作日志
 * @returns {Object}
 */
const getUcaseLogs = (id) => {
  return axios
    .get(`/api/v1/projects/${getProjectId()}/log/${id}`)
    .then(checkResponse)
}

/**
 * @function getUcaseRecommend
 * @desc 获取推荐流程
 * @returns {Object}
 */
const getUcaseRecommend = (id,type)=>{
   return axios
    .get(`/api/v1/projects/${getProjectId()}/similarity/recommend/${id}?type=${type}`)
    .then(checkResponse)
}

/**
 * @function getUcaseRecommendMulti
 * @desc  批量获取推荐流程
 * @returns {Object}
 */
const getUcaseRecommendMulti = (ids,type)=>{
   return axios
    .get(`/api/v1/projects/${getProjectId()}/similarity/recommend?ids=${ids}&type=${type}`)
    .then(checkResponse)
}

/**
 * @function getUcaseRecommend
 * @desc 获取推荐流程详情
 * @returns {Object}
 */
const getUcaseRecommendDetail = (id)=>{
   return axios
    .get(`/api/v1/projects/${getProjectId()}/similarity/recommendDetail/${id}`)
    .then(checkResponse)
}

/**
 * @function getUcaseImportGis
 * @desc 导入地块
 * @returns {Object}
 */
const getUcaseImportGis = (file) => {
  return axios({
    url: `/api/v1/projects/${getProjectId()}/area/import_from_gis`,
    method: 'post',
    data: file,
  }).then(checkResponse)
}

const getEditOptions = (url) => {
   return axios
    .get(`/api/v1/${url}`)
    .then(checkResponse)
}

/**
 * @function addCaseRead
 * @desc 添加已读记录
 * @returns {Object}
 */
const addCaseRead = (ucaseId, organizationId) => {
   return axios
    .get(`/api/v1/projects/${getProjectId()}/targets_map/caseRead?ucaseId=${ucaseId}&organizationId=${organizationId}`)
    .then(checkResponse)
}

/**
 * @function uploadJSFile
 * @desc 导入接诉即办
 * @returns {Object}
 */
const uploadJSFile = (query,file) => {
  return axios({
    url: `/api/v1/projects/${getProjectId()}/connection/jieshu/file?${query}`,
    method: 'post',
    data: file,
  }).then(checkResponse)
}

/**
 * @function getJSWord
 * @desc 导出接诉即办word
 * @returns {Object}
 */
const getJSWord= (id)=>{
   return axios
    .get(`/api/v1/projects/${getProjectId()}/connection/jieshu/word?caseId=${id}`)
    .then(checkResponse)
}

/**
 * @function postJSWord
 * @desc 导入西城接诉即办word
 * @returns {Object}
 */
 const postJSWord= (data)=>{
  return axios
   .post(`/api/v1/projects/${getProjectId()}/connection/jieshu/words`, data)
   .then(checkResponse)
}

/**
 * @function getStageType
 * @desc 获取地块
 * @returns {Object}
 */
function getBigData(id) {
  return axios.get(`/api/v1/projects/${id}/area/geojson`).then(checkResponse);
}

/**
 * @function getYPstatistics
 * @desc 获取研判统计
 * @returns {Object}
 */
function getYPstatistics(query) {
  return axios.get(`/api/v1/projects/${getProjectId()}/reports/order/statistics${query ? `?${query}` : ''}`).then(checkResponse);
}

/**
 * @function importYP
 * @desc 导入研判文件
 * @returns {Object}
 */
const importYP = (file) => {
  return axios({
    url: `/api/v1/projects/${getProjectId()}/connection/jieshu/studyFiles`,
    method: 'post',
    data: file,
  }).then(checkResponse)
}

/**
 * @function updateUcase 编辑案件
 * @param {Number} id ucase id
 * @param {Object} data
 * @returns {Object} res {deatil}
 */
const postJScases = (data) => {
  return axios
    .post(`/api/v1/projects/${getProjectId()}/connection/jieshu/postJiesuCase`,  data )
    .then(checkResponse)
}

/**
 * @function getStageType
 * @desc 获取地块
 * @returns {Object}
 */
function getCurrentSlugStage() {
  return axios.get(`/api/v1/projects/${getProjectId()}/config/permissions`).then(checkResponse);
}

/**
 * @function postFWcases
 * @desc 房屋导入
 * @returns {Object}
 */
function postFWcases(data) {
  return axios.post(`/api/v1/projects/${getProjectId()}/connection/xin_zheng_shi/file`, data).then(checkResponse);
}

/**
 * @function postMark
 * @desc 新增备注信息
 * @returns {Object}
 */
function postMark(id,data) {
  return axios.post(`/api/v1/projects/${getProjectId()}/ucases/${id}/active_logs/marks`, data).then(checkResponse);
}

/**
 * @function getMark
 * @desc 获取备注信息
 * @returns {Object}
 */
function getMark(id,page) {
  return axios.get(`/api/v1/projects/${getProjectId()}/ucases/${id}/active_logs/marks?pagination=true&per=10&page=${page}`).then(checkResponse);
}

/**
 * @function getMark
 * @desc 获取备注信息
 * @returns {Object}
 */
function getToolMap(id) {
  return axios.get(`/api/v1/projects/${getProjectId()}/cases_scoring/plotTools/${id}`).then(checkResponse);
}

/**
 * @function getMark
 * @desc 获取备注信息
 * @returns {Object}
 */
function postToolMap(data) {
  return axios.post(`/api/v1/projects/${getProjectId()}/cases_scoring/plotTools`, data).then(checkResponse);
}

/**
 * @function getMark
 * @desc 获取备注信息
 * @returns {Object}
 */
function putToolMap(data) {
  return axios.put(`/api/v1/projects/${getProjectId()}/cases_scoring/plotTools`, data).then(checkResponse);
}

/**
 * @function batchCancel
 * @desc 批量撤销
 * @returns {Object}
 */
function batchCancel(data) {
  return axios.post(`/api/v1/projects/${getProjectId()}/ucases/batch_cancel`, data).then(checkResponse);
}

/**
 * @function getNotify
 * @desc 查询通知案件
 * @returns {Object}
 */
function getNotify(type) {
  return axios.get(`/api/v1/projects/${getProjectId()}/ucases/notify_poll?type=${type}`).then(checkResponse);
}

/**
 * @function putNotify
 * @desc 通知完成回调
 * @returns {Object}
 */
function putNotify(ids, type) {
  return axios.post(`/api/v1/projects/${getProjectId()}/ucases/notified?type=${type}`,{ids:ids}).then(checkResponse);
}

function getIfCase(ucaseId) {
  return axios.get(`/api/case/typical/case/exists?projectId=${getProjectId()}&ucaseId=${ucaseId}`).then(checkResponse);
}

// 批量标记案件
function BulkMarks(query) {
  return axios.post(`/api/v1/projects/${getProjectId()}/ucases/bulk_change_status`,query).then(checkResponse);
}

// 批量回访
function BulkHuiFang(file) {
  return axios.post(`/api/v1/projects/${getProjectId()}/connection/xin_zheng_shi/filesl`,file).then(checkResponse);
}

// 港中投导入案件
function GztImport(file) {
  return axios.post(`/api/v1/projects/${getProjectId()}/connection/xin_zheng_shi/filegzt`,file).then(checkResponse);
}

// 玉溪督办
function YXCall(ucaseId) {
  return axios.post(`/api/v1/projects/${getProjectId()}/ucases/${ucaseId}/yx_manually_supervise_handle`).then(checkResponse);
}

// 大兴创城统计
function dxStatistics(query) {
  return axios.get(`/api/v1/projects/${getProjectId()}/cases_scoring/org_ucase_overview?${query}`).then(checkResponse);
}

// 通州重点统计地块
function pointData(taskId, params={}) {
  return axios.get(`/api/v1/projects/${getProjectId()}/tasks/${taskId}/ucases/tz_focal_point_group_by_area`, {params}).then(checkResponse);
}

// 通州重点统计详情
function pointCase(taskId, params={}) {
  return axios.get(`/api/v1/projects/${getProjectId()}/tasks/${taskId}/ucases`, {params}).then(checkResponse);
}

// 石景山考核
function getScoreData(params={}) {
  return axios.get(`/api/v1/projects/${getProjectId()}/ucases/shijingshan_get_scores`, {params}).then(checkResponse);
}

// 石景山考核编辑
function putScoreData(params) {
  return axios.put(`/api/v1/projects/${getProjectId()}/ucases/shijingshan_update_scores`, params).then(checkResponse);
}

//大兴创城短信通知接口
function postMessage(mobiles) {
  return axios.post(`/api/v1/projects/${getProjectId()}/ucases/daxing_short_message_notify`, {mobiles}).then(checkResponse);
}

//港中投责任单位弹窗
function fetchGztMessage() {
  return axios.get(`/api/v1/projects/${getProjectId()}/ucases/resorg_popup_info`).then(checkResponse);
}

// 河湖案件统计
function fetchCaseCount() {
  return axios.get(`/api/v1/connection/caseCount`).then(checkResponse);
}

export {
  getUcaseAreaCount,
  getUcaseStandardCount,
  getCountByStage,
  getUcaseDetails,
  getUcaseList,
  getUcaseReviews,
  getUcaseStatusList,
  getUcaseTabs,
  getUcaseMap,
  postUcaseFile,
  deleteUcase,
  updateUcase,
  deleteUcaseAction,
  createUcase,
  postUcaseReviews,
  caseDetails,
  checkIsExistUcase,
  getUcaseQuickList,
  revokeUcaseAction,
  getDeleteUcaseList,
  getDeleteUcaseTabs,
  getShunyiReport,
  getUcaseAreaRoad,
  getUcaseEarlyWarningConfig,
  getUcaseListByPost,
  getUcaseLogs,
  getUcaseSearchOption,
  recoverUcase,
  getUcaseColumns,
  getUcaseRecommend,
  getUcaseRecommendDetail,
  getUcaseImportGis,
  getUcaseRecommendMulti,
  getEditOptions,
  postJHFile,
  addCaseRead,
  uploadJSFile,
  getJSWord,
  getBigData,
  importYP,
  getYPstatistics,
  postJScases,
  getCurrentSlugStage,
  postFWcases,
  updateReview,
  postMark,
  getMark,
  getToolMap,
  postToolMap,
  putToolMap,
  updateCommonReview,
  getQuickUcaseList,
  batchCancel,
  getNotify,
  putNotify,
  getIfCase,
  postJSWord,
  BulkMarks,
  BulkHuiFang,
  GztImport,
  YXCall,
  dxStatistics,
  pointData,
  pointCase,
  getScoreData,
  putScoreData,
  postMessage,
  fetchGztMessage,
  fetchCaseCount
}
